import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { AppDefaultStateProps } from '@store/reducers';
import { AlertEntity } from '@src/types/Alerts';

import { Incident, IncidentMaxCellTMonitorProps } from '../types';
import {
  createIncidentFromAlert,
  deduplicateIncidents,
} from '../utils/incidentUtils';

dayjs.extend(utc);

const MAX_CELL_T_ALERT_PATTERNS = ['MaxCellT_Rack_Incident'] as const;

export interface UseMaxCellTMonitorReturn {
  incidents: ReadonlyArray<Incident>;
  loading: false;
  error: null;
  lastDataUpdate: Date | null;
}

function isMaxCellTAlert(alert: AlertEntity): boolean {
  if (alert.config?.alertName) {
    const alertName = alert.config.alertName.toLowerCase();

    const matchesAlertName = MAX_CELL_T_ALERT_PATTERNS.some((pattern) =>
      alertName.includes(pattern.toLowerCase()),
    );

    if (matchesAlertName) {
      return true;
    }
  }

  return false;
}

function sortIncidents(incidents: Incident[]): Incident[] {
  return incidents.sort((a, b) => {
    const timeCompare =
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    if (timeCompare !== 0) {
      return timeCompare;
    }
    return b.maxCellT - a.maxCellT;
  });
}

function areIncidentsEqual(
  prev: ReadonlyArray<Incident>,
  next: ReadonlyArray<Incident>,
): boolean {
  if (prev.length !== next.length) {
    return false;
  }

  for (let i = 0; i < prev.length; i++) {
    const prevIncident = prev[i];
    const nextIncident = next[i];

    if (
      prevIncident.id !== nextIncident.id ||
      prevIncident.deviceName !== nextIncident.deviceName ||
      prevIncident.rackId !== nextIncident.rackId ||
      prevIncident.maxCellT !== nextIncident.maxCellT ||
      prevIncident.timestamp !== nextIncident.timestamp ||
      prevIncident.unitId !== nextIncident.unitId
    ) {
      return false;
    }
  }

  return true;
}

export function useMaxCellTMonitor(
  props: IncidentMaxCellTMonitorProps,
): UseMaxCellTMonitorReturn {
  const { siteMeta, selectedUnits } = props;

  const [lastDataUpdate, setLastDataUpdate] = useState<Date | null>(null);
  const previousIncidentsRef = useRef<ReadonlyArray<Incident>>([]);

  const activeAlerts = useSelector(
    (state: AppDefaultStateProps) => state.alerts?.activeAlerts || [],
    (prevAlerts, nextAlerts) => {
      const prevMaxCellT = prevAlerts.filter(isMaxCellTAlert);
      const nextMaxCellT = nextAlerts.filter(isMaxCellTAlert);

      if (prevMaxCellT.length !== nextMaxCellT.length) {
        return false;
      }

      return prevMaxCellT.every((prevAlert, index) => {
        const nextAlert = nextMaxCellT[index];
        if (!nextAlert) {
          return false;
        }
        return (
          prevAlert.config?.id === nextAlert.config?.id &&
          prevAlert.config?.alertName === nextAlert.config?.alertName &&
          prevAlert.ts === nextAlert.ts &&
          prevAlert.value === nextAlert.value
        );
      });
    },
  );

  const incidents = useMemo(() => {
    if (!activeAlerts || activeAlerts.length === 0) {
      return [];
    }

    const maxCellTAlerts = activeAlerts.filter(isMaxCellTAlert);
    const newIncidents = maxCellTAlerts.map((alert) =>
      createIncidentFromAlert(alert, siteMeta),
    );

    const deduplicated = deduplicateIncidents(newIncidents);
    const sorted = sortIncidents(deduplicated);

    return sorted;
  }, [activeAlerts, siteMeta]);

  const filteredIncidents = useMemo(() => {
    const uniqueIncidents = deduplicateIncidents(incidents);

    if (!selectedUnits || selectedUnits.length === 0) {
      return uniqueIncidents;
    }

    const selectedUnitsSet = new Set(selectedUnits.map(String));

    return uniqueIncidents.filter((incident) =>
      selectedUnitsSet.has(incident.unitId),
    );
  }, [incidents, selectedUnits]);

  // Prevent unnecessary updates with deep comparison
  useEffect(() => {
    if (!areIncidentsEqual(previousIncidentsRef.current, filteredIncidents)) {
      previousIncidentsRef.current = filteredIncidents;
      if (filteredIncidents.length > 0) {
        setLastDataUpdate(new Date());
      }
    }
  }, [filteredIncidents]);

  return {
    incidents: filteredIncidents,
    loading: false,
    error: null,
    lastDataUpdate,
  };
}
